// src/pages/customer/CustomerCards.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { loyalty as loyaltyApi, favorites as favApi } from '../../api';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { formatMoney } from '../../data/currencies';
import { CUSTOMER_NAV, useCustomerNav } from './CustomerHome';
import { ReceiptRow } from './CustomerReceipt';

export default function CustomerCards() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navItems = useCustomerNav();
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setCards(await loyaltyApi.cards()); } catch (e) {}
    setLoading(false);
  };

  const openCard = async (card) => {
    setSelected(card);
    setHistLoading(true);
    setReceipts([]);
    const [h, r] = await Promise.all([
      loyaltyApi.history(card.shopId).catch(() => []),
      loyaltyApi.receipts(card.shopId).catch(() => []),
    ]);
    setHistory(Array.isArray(h) ? h : []);
    setReceipts(Array.isArray(r) ? r : []);
    setHistLoading(false);
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  if (selected) {
    const shop = selected.shop;
    const minPoints = shop?.minPointsToRedeem || 500;
    const progress = Math.min(100, ((selected.points % minPoints) / minPoints) * 100);

    return (
      <div className="page">
        <div className="page-header">
          <button className="back-btn" onClick={() => setSelected(null)}>
            <Icon name="chevronLeft" size={18} />
          </button>
          <h1 className="page-title" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shop?.name || selected.shopId}
          </h1>
        </div>

        <div style={{ padding: '0 20px' }}>
          {/* Большая карта баллов */}
          <div className="hero-loyalty" style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: '0.08em' }}>{(shop?.name || '').toUpperCase()}</p>
            <p className="hero-num" style={{ fontSize: 52, marginTop: 8 }}>
              {(selected.points || 0).toLocaleString()}
            </p>
            <p style={{ fontSize: 14, opacity: 0.85, marginTop: 2 }}>{t("customerCards.pointsLabel")}</p>
            <div style={{
              marginTop: 16, paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', gap: 24,
            }}>
              <div>
                <p style={{ fontSize: 11, opacity: 0.7 }}>{t("customerCards.purchases")}</p>
                <p className="num" style={{ fontSize: 18, fontWeight: 700 }}>{selected.totalPurchases || 0}</p>
              </div>
              <div>
                <p style={{ fontSize: 11, opacity: 0.7 }}>{t("customerCards.spent")}</p>
                <p className="num" style={{ fontSize: 18, fontWeight: 700 }}>{formatMoney(selected.totalSpent || 0, selected.shop?.currency)}</p>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/shop/${selected.shopId}`)}>
              <Icon name="store" size={14} />
              {t('customerCards.storefront')}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={async () => {
              try { await favApi.add(selected.shopId); } catch (e) {}
            }}>
              <Icon name="star" size={14} />
              {t('customerCards.addToFavs')}
            </button>
          </div>

          {shop && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <p style={{ fontSize: 13, color: "var(--fg-muted)" }}>{t("customerCards.untilDiscount")}</p>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-accent)', fontVariantNumeric: 'tabular-nums' }}>
                  {t("customerCards.pointsLeft", { count: Math.max(0, minPoints - (selected.points % minPoints)).toLocaleString() })}
                </p>
              </div>
              <div style={{ background: 'var(--bg-subtle)', borderRadius: 6, height: 6, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, var(--brand-primary), var(--brand-accent))',
                }} />
              </div>
            </div>
          )}

          {/* Чеки покупок в этом магазине */}
          {receipts.length > 0 && (
            <>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 10, letterSpacing: '0.08em' }}>
                {t('receipt.listLabel')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {receipts.map(r => <ReceiptRow key={r.id} r={r} onClick={() => navigate(`/receipt/${r.id}`)} />)}
              </div>
            </>
          )}

          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 10, letterSpacing: '0.08em' }}>
            {t("customerCards.historyLabel")}
          </p>
          {histLoading ? (
            <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }}/></div>
          ) : history.length === 0 ? (
            <div className="empty-state"><p>{t("customerCards.historyEmpty")}</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map(h => {
                const isEarn = h.type === 'earn';
                return (
                  <div key={h.id} className="card"
                    onClick={h.saleId ? () => navigate(`/receipt/${h.saleId}`) : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: h.saleId ? 'pointer' : undefined }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 'var(--r-sm)',
                      background: isEarn ? 'var(--success-soft)' : 'var(--brand-accent-soft)',
                      color: isEarn ? 'var(--success)' : 'var(--brand-accent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon name={isEarn ? 'plus' : 'starFilled'} size={16} stroke={isEarn ? 2.5 : 0} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 600 }}>
                        {isEarn ? t('customerCards.earned') : t('customerCards.redeemed')}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                        {new Date(h.createdAt).toLocaleDateString('ru', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {h.saleTotal ? ` · ${formatMoney(h.saleTotal, selected.shop?.currency)}` : ''}
                      </p>
                    </div>
                    <p style={{ fontSize: 18, fontWeight: 800, color: isEarn ? 'var(--success)' : 'var(--danger)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                      {isEarn ? '+' : '−'}{h.points}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <BottomNav items={navItems} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="page-subtitle">{t('shopHome.loyalty')}</p>
          <h1 className="page-title">{t('customerCards.title')}</h1>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        {cards.length === 0 ? (
          <div className="empty-state">
            <div className="icon"><Icon name="creditCard" size={28} stroke={1.5} /></div>
            <p>{t('customerCards.noCards')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {cards.map(card => {
              const minPoints = card.shop?.minPointsToRedeem || 500;
              const progress = Math.min(100, ((card.points % minPoints) / minPoints) * 100);
              return (
                <div key={card.id} className="card" style={{ cursor: 'pointer' }} onClick={() => openCard(card)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {card.shop?.name || card.shopId}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                        {t('customerCards.purchasesShort', { count: card.totalPurchases || 0 })}
                      </p>
                    </div>
                    <p style={{ fontSize: 24, fontWeight: 800, color: 'var(--brand-accent)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                      {(card.points || 0).toLocaleString()}
                    </p>
                  </div>
                  <div style={{ marginTop: 12, background: 'var(--bg-subtle)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, var(--brand-primary), var(--brand-accent))',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <BottomNav items={navItems} />
    </div>
  );
}
